import { createTheme } from '@mui/material';
import React from 'react';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    legend: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    legend?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    legend: true;
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#356C89',
    },
  },
  typography: {
    fontFamily: 'DM Sans',
    h1: {
      fontStyle: 'normal',
      fontWeight: '500',
      fontSize: '72px',
      lineHeight: '80px',
      letterSpacing: '-0.04em',
    },
    h2: {
      fontStyle: 'normal',
      fontWeight: '500',
      fontSize: '52px',
      lineHeight: '60px',
      letterSpacing: '-0.03em',
    },
    h3: {
      fontStyle: 'normal',
      fontWeight: '500',
      fontSize: '44px',
      lineHeight: '52px',
      letterSpacing: '-0.02em',
    },
    h4: {
      fontStyle: 'normal',
      fontWeight: '500',
      fontSize: '36px',
      lineHeight: '44px',
      letterSpacing: '-0.02em',
    },
    h5: {
      fontStyle: 'normal',
      fontWeight: '700',
      fontSize: '22px',
      lineHeight: '28px',
      letterSpacing: '-0.01em',
    },
    h6: {
      fontStyle: 'normal',
      fontWeight: '700',
      fontSize: '14px',
      lineHeight: '18px',
      letterSpacing: '-0.01em',
    },
    subtitle1: {
      fontWeight: '500',
      fontSize: '16px',
      lineHeight: '20px',
    },
    subtitle2: {
      fontStyle: 'normal',
      fontWeight: '500',
      fontSize: '14px',
      lineHeight: '18px',
      // TODO: this is not ok, will need refactor later
      '&.labelHeader': {
        color: '#B6B6B6',
      },
      '&.labelValue': {
        color: '#4B4B4B',
      },
    },
    body1: {
      fontStyle: 'normal',
      fontWeight: '400',
      fontSize: '14px',
      lineHeight: '14px',
    },
    body2: {
      fontStyle: 'normal',
      fontWeight: '400',
      fontSize: '14px',
      lineHeight: '14px',
      color: '#666',
    },
    legend: {
      fontWeight: 500,
      fontSize: '20px',
      lineHeight: '30px',
      color: '#4B4B4B',
    },
    caption: {
      fontWeight: 400,
      fontSize: '14px',
      color: '#B6B6B6',
    },
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h2',
          h2: 'h2',
          h3: 'h2',
          h4: 'h2',
          h5: 'h2',
          h6: 'h2',
          subtitle1: 'span',
          subtitle2: 'span',
          body1: 'span',
          body2: 'span',
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          paddingLeft: '0px !important',
          paddingRight: '5px !important',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
          boxShadow: 'none',
          marginBottom: '10px',
        },
      },
    },

    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '36px 40px 0 40px',
        },
        title: {
          fontSize: '18px',
          fontWeight: 500,
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '40px',
          '&:last-child': {
            paddingBottom: '40px',
          },
        },
      },
    },

    MuiLink: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          textDecoration: 'underline',
          color: '#4B4B4B',
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: '15px',
          textAlign: 'center',
          fontFamily: 'DM Sans',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: '20px',
        },
        containedPrimary: {
          background: '#0F253B',
          color: '#fff',
          boxShadow: '0px 3px 0px 0px rgba(11, 34, 66, 0.20)',
          '&:hover': {
            background: '#3D4F61',
          },
        },
        outlinedPrimary: {
          background: '#0F253B',
          color: '#fff',
          boxShadow: '0px 3px 0px 0px rgba(11, 34, 66, 0.20)',
          '&:hover': {
            background: '#3D4F61',
          },
        },
        containedSecondary: {
          background: '#fff',
          color: '#0F253B',
          border: '1px solid #C7CCD4',
          boxShadow: '0px 3px 0px 0px rgba(11, 34, 66, 0.20)',
          '&:hover': {
            background: '#F5F6FA',
            border: '1px solid #C7CCD4',
          },
        },
        outlinedSecondary: {
          background: '#fff',
          color: '#0F253B',
          border: '1px solid #C7CCD4',
          boxShadow: '0px 3px 0px 0px rgba(11, 34, 66, 0.20)',
          '&:hover': {
            background: '#F5F6FA',
            border: '1px solid #C7CCD4',
          },
        },
        sizeLarge: {
          padding: '21px 27px',
          height: '58px',
        },
        sizeMedium: {
          padding: '21px 27px',
          height: '48px',
        },
        sizeSmall: {
          padding: '21px 27px',
          height: '38px',
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: 'rgba(26, 174, 78, .2)',
          color: '#1AAE4E',
        },
        colorSecondary: {
          backgroundColor: 'rgba(255, 163, 74, .2)',
          color: '#FFA34A',
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: {
          background: '#FFFFFF',
          border: '1px solid #EAEAEA',
          borderRadius: '10px',
        },
        indicator: {
          display: 'none',
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          padding: '14px 27px',
          fontSize: '14px',
          lineHeight: '20px',
          color: '#000',
          textTransform: 'none',
          transition: '.5s all',
          '&.Mui-selected': {
            background: '#FF6D63',
            color: '#FFFFFF',
            borderRadius: '10px',
          },
        },
      },
    },

    MuiFormControl: {
      styleOverrides: {
        root: {
          marginTop: '20px',
        },
        marginDense: {
          marginTop: 0,
          marginBottom: 0,
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#B6B6B6',
          paddingBottom: 0,
        },
        formControl: {
          display: 'flex',
          position: 'relative',
          transform: 'none',
          fontSize: '14px',
          lineHeight: '20px',
          color: '#000',
          paddingBottom: '10px',
          marginTop: 0,
          '&.Mui-focused': {
            color: '#000',
          },
          '&::after': {
            content: '"(optional)"',
            color: '#4B4B4B',
            marginLeft: '5px',
          },
          '&.Mui-required::after': {
            content: '"*"',
          },
        },
        asterisk: {
          display: 'none',
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        standard: {
          padding: '14px 16px',
          background: 'transparent',
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          '&.Mui-error': {
            borderColor: '#d32f2f',
          },
        },
        formControl: {
          background: '#FFF',
          border: '1px solid #EAEAEA',
          borderRadius: '8px',
          height: '48px',
          color: '#4B4B4B',
          '&.Mui-focused': {
            border: '1px solid #4B4B4B',
          },
          '.MuiOutlinedInput-notchedOutline': {
            display: 'none',
          },
        },
        sizeSmall: {
          height: '32px',
        },
      },
    },

    MuiFormLabel: {
      styleOverrides: {
        root: {
          paddingBottom: '10px',
          color: '#000',
          fontSize: '14px',
          lineHeight: '20px',
          '&.MuiInputLabel-root::after': {
            content: '""',
          },
        },
      },
    },

    MuiToggleButtonGroup: {},

    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          '&.Mui-selected, &.Mui-selected:hover': {
            color: 'white',
            backgroundColor: '#3E7B9A',
          },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        head: {
          color: '#3E4144',
          fontStyle: 'normal',
          fontWeight: '700',
          fontSize: '14px',
          lineHeight: '24px',
          background: '#F5F6FA',
        },
        body: {
          color: '#3E4144',
          fontStyle: 'normal',
          fontWeight: '400',
          fontSize: '12px',
          lineHeight: '17px',
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          margin: '3px 0',
          fontSize: '12px',
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          padding: '18px 27px 18px 18px',
          alignItems: 'center',
        },
        icon: {
          padding: 0,
          marginRight: '14px',
        },
        standardInfo: {
          background: '#DFF1F7',
          color: '#000',
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        root: {
          '& .MuiMenu-paper': {
            marginTop: '-2px',
            borderRadius: '0 0 10px 10px',
            border: '1px solid #F6F6F7',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
            background: '#fff',
          },
          '& .MuiList-root': {
            padding: 0,
          },
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderTop: '1px solid #EAEAEA',
          justifyContent: 'center',
          padding: '16px',
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: {},
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          display: 'flex',
          flexGrow: 1,
          color: '#3E4144',
          padding: '40px 40px 0 40px',
          justifyContent: 'center',
          textAlign: 'center',
          fontSize: '28px',
          fontWeight: 700,
          lineHeight: '34px',
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '40px 40px 0 40px',
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '40px',
        },
      },
    },
  },
});

export default theme;
